import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, NgZone, inject } from '@angular/core';
import { environment } from '@env/environment';
import { ToastService } from '../services';

const HTTP_MESSAGES: Record<number, string> = {
  [0]: 'Serviço indisponível. Verifique sua conexão.',
  [401]: 'Sessão expirada. Faça login novamente.',
  [403]: 'Você não tem permissão para realizar esta ação.',
  [404]: 'Recurso não encontrado.',
  [422]: 'Dados inválidos. Verifique as informações enviadas.',
  [429]: 'Muitas requisições. Tente novamente em instantes.',
  [500]: 'Erro interno no servidor. Tente novamente mais tarde.',
  [503]: 'Serviço temporariamente indisponível.',
};

@Injectable({ providedIn: 'root' })
export class ErrorHandlerUser implements ErrorHandler {
  private readonly zone = inject(NgZone);
  private readonly toastService = inject(ToastService);

  public handleError(error: unknown): void {
    const err = this.unwrap(error);

    if (this.isAngularInternalError(err)) {
      console.error('[ErrorHandler]', err);
      return;
    }

    if (!environment.production) {
      console.error('[ErrorHandler]', err);
    }

    try {
      this.zone.run(() => this.dispatch(err));
    } catch {
      // Prevent infinite loops if the toast service itself fails
    }
  }

  private dispatch(error: unknown): void {
    if (this.isChunkLoadError(error)) {
      this.toastService.warning({ message: 'Nova versão disponível. Recarregue a página para continuar.' });
      return;
    }

    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error);
      return;
    }

    if (this.isApiErrorBody(error)) {
      const message: string = (error as any).message ?? 'Ocorreu um erro inesperado.';
      this.toastService.error({ message });
      return;
    }

    if (error instanceof Error) {
      const message = environment.production ? 'Ocorreu um erro inesperado.' : error.message;
      this.toastService.error({ message });
      return;
    }

    this.toastService.error({ message: 'Ocorreu um erro inesperado.' });
  }

  private handleHttpError(error: HttpErrorResponse): void {
    const message = HTTP_MESSAGES[error.status] ?? `Erro ${error.status}: ${error.statusText}`;
    this.toastService.error({ message });
  }

  /** Angular wraps unhandled promise rejections as { rejection: ... } */
  private unwrap(error: unknown): unknown {
    if (error !== null && typeof error === 'object' && 'rejection' in error) {
      return (error as any).rejection;
    }
    return error;
  }

  private isChunkLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return (
      error.name === 'ChunkLoadError' || /loading chunk \d+ failed/i.test(error.message) || /failed to fetch dynamically imported module/i.test(error.message)
    );
  }

  /** Detects the error body thrown by ApiService (plain object with status + message) */
  private isApiErrorBody(error: unknown): boolean {
    return error !== null && typeof error === 'object' && 'status' in error && 'message' in error && !(error instanceof HttpErrorResponse);
  }

  /**
   * Angular internal/programming errors (e.g. NG01052 — wrong FormGroup setup).
   * These are developer mistakes and should never reach the end user as toasts.
   */
  private isAngularInternalError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return /^NG\d+:/i.test(error.message) || error.name === 'RuntimeError';
  }
}
